import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { ApiError } from "@bw/shared/api";
import { authorLabel, authorInitials, MISSING } from "@bw/shared/components";
import type {
  CommunityResponse,
  ConversationResponse,
  ConversationParticipantSummary,
  PlayerCard as PlayerCardData,
  PostResponse,
} from "@bw/shared/types/generated";
import { useConversations } from "../hooks/useConversations";
import { usePlayerSearch } from "../hooks/usePlayerSearch";
import { useCreateConversation } from "../hooks/useCreateConversation";
import { useSendMessage, type MessageSendVars } from "../hooks/useSendMessage";
import { useCommunities } from "../hooks/useCommunities";
import { useCreateRepost } from "../hooks/useCreateRepost";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

/**
 * PostShareDialog — the "Share post" experience, modelled on LinkedIn's share
 * sheet: a WAI-ARIA modal (role="dialog", aria-modal, a labelled title, Escape /
 * scrim / Close, a Tab focus trap + body-scroll lock via useFocusTrap) that opens
 * from the post action bar's "Share" control. It leads with a plain ACTION LIST —
 * NOT a segmented tablist — so choosing a destination is a single, obvious tap and
 * each destination mounts only when picked:
 *
 *   • Repost — instantly reshare to YOUR feed (no quote). Fires on the menu row.
 *   • Repost with your thoughts — a quote composer → your feed.
 *   • Send as message — share the post into a DIRECT MESSAGE as a first-class EMBED
 *     (a structured `shared_post_id` on the message, NEVER a URL in the text). Pick
 *     an existing conversation, or search a player (get-or-create the 1:1, then send).
 *   • Share to a community — repost the post INTO a community's feed (an optional
 *     quote), lists ONLY communities you can post to (the backend re-validates).
 *
 * On your OWN (root-authored) post every repost path is a self-repost, which the
 * backend rejects (A5-BE), so only "Send as message" is offered — and, being the
 * lone action, it opens directly (no menu). All repost / DM / community hooks fire
 * only for the mounted view, so a feed of cards never fans out a fetch per card.
 *
 * Replacing the old roving tablist (whose `onFocus`+`flushSync` tore the focused
 * chat search input down mid-flush, crashing the dialog on real browsers) with this
 * list also fixes that regression: no destination switch tears down a focused input.
 *
 * no-UID-in-UI: every name is uid-safe (authorLabel → em-dash, never a raw uid); a
 * post-detail HREF may carry the post_id (a URL, not visible text); the viewer's uid
 * is NEVER woven into any payload (chat content or repost).
 */
export function PostShareDialog({
  post,
  open,
  onClose,
}: {
  post: PostResponse;
  open: boolean;
  onClose: () => void;
}) {
  if (!open) return null;
  return <PostShareDialogInner post={post} onClose={onClose} />;
}

/** Debounce (ms) before a typed query re-fires the player search (mirrors the
 *  compose-message dialog). */
const SEARCH_DEBOUNCE_MS = 250;

/** The dialog's current view: the LinkedIn-style menu, or one destination's panel.
 *  ("repost" — the instant reshare — is an ACTION fired from the menu row, not a
 *  panel, so it is not a view.) */
type ShareView = "menu" | "repost-quote" | "chat" | "community";

/** The per-view accessible dialog title. */
const VIEW_TITLE: Record<ShareView, string> = {
  menu: "Share post",
  "repost-quote": "Repost with your thoughts",
  chat: "Send as message",
  community: "Share to a community",
};

function PostShareDialogInner({
  post,
  onClose,
}: {
  post: PostResponse;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const me = user?.uid;

  // A5-FE — on your OWN post the reshare would be a self-repost (the post you'd
  // repost is the flattened ROOT original, so the gate is its author), which the
  // backend rejects. Mirrors PostActionBar's canRepost gate. When it is your post,
  // only "Send as message" is available.
  const rootAuthorId = post.reposted_post?.author_id ?? post.author_id;
  const canRepost = !!me && rootAuthorId !== me;

  // Menu order (LinkedIn: repost group, then message, then community). On your own
  // post only chat survives → open it directly (no menu, Back closes).
  const single = !canRepost;
  const [view, setView] = useState<ShareView>(single ? "chat" : "menu");

  // A mutation is in flight in the active view. Lifted so the shell blocks Esc /
  // scrim / Close / Back during a half-open repost, send, or share.
  const [busy, setBusy] = useState(false);

  // Back from a destination panel → the menu (disabled while busy). When only the
  // one action exists there is no menu, so Back closes the dialog.
  function goBack() {
    if (busy) return;
    if (single) onClose();
    else setView("menu");
  }

  const showBack = view !== "menu" && !single;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={busy ? undefined : onClose}
      aria-labelledby="post-share-title"
    >
      <DialogTitle id="post-share-title" sx={{ px: showBack ? 7 : 3, pr: 7 }}>
        {showBack ? (
          <IconButton
            type="button"
            onClick={goBack}
            disabled={busy}
            aria-label="Back to share options"
            sx={{ position: "absolute", left: 10, top: 10 }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
        ) : null}
        {VIEW_TITLE[view]}
        <IconButton
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label="Close"
          title="Close"
          sx={{ position: "absolute", right: 10, top: 10 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {view === "menu" ? (
          <ShareMenu
            post={post}
            canRepost={canRepost}
            onPick={setView}
            onClose={onClose}
            onBusyChange={setBusy}
          />
        ) : view === "repost-quote" ? (
          <RepostQuoteMode
            post={post}
            onClose={onClose}
            onBusyChange={setBusy}
          />
        ) : view === "chat" ? (
          <ChatShareMode post={post} onClose={onClose} onBusyChange={setBusy} />
        ) : (
          <CommunityShareMode
            post={post}
            onClose={onClose}
            onBusyChange={setBusy}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// The LinkedIn-style action list (the default view)
// =============================================================================

function ShareMenu({
  post,
  canRepost,
  onPick,
  onClose,
  onBusyChange,
}: {
  post: PostResponse;
  canRepost: boolean;
  onPick: (view: Exclude<ShareView, "menu">) => void;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  // The instant "Repost" fires its mutation straight from the menu row (no panel),
  // so the menu owns it. `submittingRef` guards a same-tick double-tap (isPending
  // only flips on the next render), exactly like Mode-B / EditPostDialog.
  const repost = useCreateRepost();
  const submittingRef = useRef(false);
  const listRef = useRef<HTMLUListElement>(null);
  const pending = repost.isPending;
  useEffect(() => {
    onBusyChange(pending);
  }, [pending, onBusyChange]);

  // WAI-ARIA APG initial focus: on open (and on Back → menu) move focus to the
  // first destination so a keyboard / SR user lands INSIDE the dialog rather than
  // stranded on the now-inert Share trigger behind the scrim. The destination
  // sub-panels focus their own first field; only this menu view needed it.
  useEffect(() => {
    listRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, []);

  function handleRepost() {
    if (pending || submittingRef.current) return;
    submittingRef.current = true;
    repost.mutate(
      { postId: post.post_id },
      {
        onSuccess: () => onClose(),
        onError: () => {
          submittingRef.current = false;
        },
      },
    );
  }

  const alreadyReposted =
    repost.isError &&
    repost.error instanceof ApiError &&
    repost.error.status === 409;

  return (
    <Stack spacing={1.5}>
      <List ref={listRef} disablePadding>
        {canRepost ? (
          <ShareMenuItem
            icon={<RepeatRoundedIcon />}
            title="Repost"
            subtitle="Instantly bring this post to your feed"
            busy={pending}
            disabled={pending}
            onClick={handleRepost}
          />
        ) : null}
        {canRepost ? (
          <ShareMenuItem
            icon={<EditOutlinedIcon />}
            title="Repost with your thoughts"
            subtitle="Add a note and post it to your feed"
            disabled={pending}
            onClick={() => onPick("repost-quote")}
          />
        ) : null}
        <ShareMenuItem
          icon={<SendRoundedIcon />}
          title="Send as message"
          subtitle="Privately share with someone"
          disabled={pending}
          onClick={() => onPick("chat")}
        />
        {canRepost ? (
          <ShareMenuItem
            icon={<GroupsOutlinedIcon />}
            title="Share to a community"
            subtitle="Post it into a community's feed"
            disabled={pending}
            onClick={() => onPick("community")}
          />
        ) : null}
      </List>

      {/* Honest failure for the instant repost — a 409 already-reposted has its own
          specific copy; the dialog STAYS open either way (non-optimistic). */}
      {alreadyReposted ? (
        <Alert severity="error" role="alert">
          You&apos;ve already reposted this to your feed.
        </Alert>
      ) : repost.isError ? (
        <GuardianAwareError error={repost.error}>
          <Alert severity="error" role="alert">
            We couldn&apos;t repost that. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}
    </Stack>
  );
}

/** One row of the share menu: a big tap target with an icon, a title, and a muted
 *  one-line subtitle (the LinkedIn share-sheet idiom). */
function ShareMenuItem({
  icon,
  title,
  subtitle,
  onClick,
  disabled = false,
  busy = false,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={onClick}
        disabled={disabled}
        aria-busy={busy || undefined}
        sx={{ borderRadius: 2, py: 1.5 }}
      >
        <ListItemAvatar>
          <Avatar
            aria-hidden
            sx={{ bgcolor: "action.hover", color: "text.primary" }}
          >
            {icon}
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary={title} secondary={subtitle} />
      </ListItemButton>
    </ListItem>
  );
}

// =============================================================================
// Repost with your thoughts — a quote composer → YOUR feed (community_id = null)
// =============================================================================

function RepostQuoteMode({
  post,
  onClose,
  onBusyChange,
}: {
  post: PostResponse;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [quote, setQuote] = useState("");
  const repost = useCreateRepost();
  const submittingRef = useRef(false);
  const quoteRef = useRef<HTMLTextAreaElement>(null);

  const pending = repost.isPending;
  useEffect(() => {
    onBusyChange(pending);
  }, [pending, onBusyChange]);

  // Focus the composer on open (focus moves into the panel).
  useEffect(() => {
    quoteRef.current?.focus();
  }, []);

  const alreadyReposted =
    repost.isError &&
    repost.error instanceof ApiError &&
    repost.error.status === 409;

  function handleShare() {
    if (pending || submittingRef.current) return;
    submittingRef.current = true;
    repost.mutate(
      { postId: post.post_id, quote: quote.trim() || undefined },
      {
        onSuccess: () => onClose(),
        onError: () => {
          submittingRef.current = false;
        },
      },
    );
  }

  const author = authorLabel(post.author_display_name, post.author_id);

  return (
    <Stack spacing={2}>
      {/* What you're resharing — a quiet confirmation line (uid-safe name). */}
      <Typography color="text.secondary">
        Reposting {author === MISSING ? "this post" : <strong>{author}</strong>}
        {author === MISSING ? "" : "'s post"}
      </Typography>

      <TextField
        inputRef={quoteRef}
        label="Add your thoughts (optional)"
        placeholder="Add your thoughts (optional)"
        value={quote}
        disabled={pending}
        onChange={(e) => setQuote(e.target.value)}
        multiline
        minRows={4}
        fullWidth
      />

      {alreadyReposted ? (
        <Alert severity="error" role="alert">
          You&apos;ve already reposted this to your feed.
        </Alert>
      ) : repost.isError ? (
        <GuardianAwareError error={repost.error}>
          <Alert severity="error" role="alert">
            We couldn&apos;t repost that. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}

      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button
          type="button"
          variant="contained"
          disabled={pending}
          aria-busy={pending || undefined}
          onClick={handleShare}
        >
          {pending ? "Reposting…" : "Repost"}
        </Button>
      </Stack>
    </Stack>
  );
}

// =============================================================================
// Mode A — Send as message (the DM embed)
// =============================================================================

/** The chosen recipient — an existing conversation, or a searched player (no
 *  conversation yet). `name` is a uid-safe LABEL only (never the raw id). */
type Recipient =
  | { kind: "conversation"; conversationId: string; name: string }
  | { kind: "player"; userId: string; name: string };

function ChatShareMode({
  post,
  onClose,
  onBusyChange,
}: {
  post: PostResponse;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { user } = useAuth();
  const me = user?.uid;

  const [note, setNote] = useState("");
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const [recipient, setRecipient] = useState<Recipient | null>(null);

  // The conversation id we will actually send to. For an existing-conversation
  // recipient it is set at send time; for a searched player it is set AFTER
  // createConversation resolves. Held in state so useSendMessage rebinds to it and
  // the send-effect fires once it exists.
  const [targetConversationId, setTargetConversationId] = useState<
    string | null
  >(null);
  // The exact payload captured at Send-click time, so a keystroke mid-send can't
  // change what gets sent. Non-null == a send is armed.
  const [pendingPayload, setPendingPayload] = useState<MessageSendVars | null>(
    null,
  );
  // A send is in flight (create + send). Guards a double-fire and drives disabled.
  const [sending, setSending] = useState(false);
  const sentRef = useRef(false);

  const conversationsQ = useConversations();
  const conversations = conversationsQ.data ?? [];
  const selectedConversation =
    recipient?.kind === "conversation"
      ? conversations.find(
          (item) => item.conversation_id === recipient.conversationId,
        )
      : undefined;
  // Resolve current server authority, not a permission snapshot captured when
  // the row was selected. A refresh may revoke an existing recipient's access.
  const recipientWritable =
    recipient?.kind === "player" ||
    (recipient?.kind === "conversation" &&
      selectedConversation?.write_access === "allowed");
  const searchQ = usePlayerSearch(debouncedQuery, "friends");
  const createConversation = useCreateConversation();
  const sendMessage = useSendMessage(targetConversationId ?? "");

  const searchRef = useRef<HTMLInputElement>(null);

  // Focus the search field on open (focus moves into the dialog).
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const pending =
    sending || createConversation.isPending || sendMessage.isPending;
  const failed = createConversation.isError || sendMessage.isError;

  // Report busy up so the shell blocks Esc / scrim / Close / Back mid-send.
  useEffect(() => {
    onBusyChange(pending);
  }, [pending, onBusyChange]);

  // Step 2 of the send: once a target conversation id exists AND a send is armed,
  // fire sendMessage EXACTLY once. The effect runs post-render, so useSendMessage
  // is freshly bound to targetConversationId (a same-tick create→send can't rebind
  // the hook, which is why this is effect-driven, not inline).
  useEffect(() => {
    if (!sending || !targetConversationId || pendingPayload === null) return;
    if (sentRef.current) return;
    sentRef.current = true;
    sendMessage.mutate(pendingPayload, {
      onSuccess: () => onClose(),
      onError: () => {
        // Keep the dialog OPEN with a role="alert"; reset so a retry can re-fire.
        setSending(false);
        setPendingPayload(null);
        sentRef.current = false;
      },
    });
  }, [sending, targetConversationId, pendingPayload, sendMessage, onClose]);

  function handleSend() {
    if (!me || !recipient || !recipientWritable || pending) return;
    // Capture the payload NOW so a mid-send keystroke can't shift it. The post
    // rides as a STRUCTURED shared_post_id (the real embed) — never a URL in text;
    // the viewer's uid is never referenced.
    setPendingPayload({ content: note.trim(), shared_post_id: post.post_id });
    setSending(true);
    if (recipient.kind === "conversation") {
      // Existing conversation → the send-effect fires once the id lands in state.
      setTargetConversationId(recipient.conversationId);
    } else {
      // Searched player → get-or-create the 1:1, THEN the effect sends to the
      // RETURNED conversation_id (never the player's user_id).
      createConversation.mutate(recipient.userId, {
        onSuccess: (conv) => setTargetConversationId(conv.conversation_id),
        onError: () => {
          setSending(false);
          setPendingPayload(null);
        },
      });
    }
  }

  // Player search results — self excluded (defense in depth; the server scopes too).
  const searchResults: PlayerCardData[] = (searchQ.data ?? []).filter(
    (p) => p.user_id !== me,
  );
  const searchActive = debouncedQuery.trim().length >= 2;

  return (
    <Stack spacing={2}>
      {/* The recipient search — start a NEW conversation, or filter to a person. */}
      <Stack
        component="form"
        role="search"
        onSubmit={(e) => e.preventDefault()}
      >
        <TextField
          inputRef={searchRef}
          type="search"
          label="Search friends to send to"
          placeholder="Search friends by name"
          value={query}
          disabled={pending}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
        />
      </Stack>

      {/* When searching → the player results; otherwise → the recent conversations. */}
      {searchActive ? (
        <Box>
          {searchQ.isError ? (
            <Alert severity="error" role="alert">
              We couldn&apos;t search right now. Please try again.
            </Alert>
          ) : searchQ.isPending || searchQ.isFetching ? (
            <Typography color="text.secondary" role="status" aria-live="polite">
              Searching…
            </Typography>
          ) : searchResults.length === 0 ? (
            <Typography color="text.secondary">No friends found.</Typography>
          ) : (
            <List disablePadding>
              {searchResults.map((player) => {
                const isSelected =
                  recipient?.kind === "player" &&
                  recipient.userId === player.user_id;
                return (
                  <PlayerRecipientRow
                    key={player.user_id}
                    player={player}
                    selected={isSelected}
                    disabled={pending}
                    onSelect={() =>
                      setRecipient({
                        kind: "player",
                        userId: player.user_id,
                        name: authorLabel(player.display_name, player.user_id),
                      })
                    }
                  />
                );
              })}
            </List>
          )}
        </Box>
      ) : (
        <Stack spacing={1}>
          <Typography variant="subtitle2">Recent conversations</Typography>
          {conversationsQ.isError && !conversationsQ.data ? (
            <Alert severity="error" role="alert">
              We couldn&apos;t load your conversations.
            </Alert>
          ) : conversations.length === 0 ? (
            <Typography color="text.secondary">
              No conversations yet — search for someone above.
            </Typography>
          ) : (
            <List disablePadding>
              {conversations.map((c) => (
                <ConversationRecipientRow
                  key={c.conversation_id}
                  conversation={c}
                  me={me}
                  disabled={pending || c.write_access !== "allowed"}
                  selected={
                    recipient?.kind === "conversation" &&
                    recipient.conversationId === c.conversation_id
                  }
                  onSelect={(name) =>
                    setRecipient({
                      kind: "conversation",
                      conversationId: c.conversation_id,
                      name,
                    })
                  }
                />
              ))}
            </List>
          )}
        </Stack>
      )}

      {/* Optional note — sent VERBATIM as the message content (may be empty). */}
      <TextField
        label="Add a message (optional)"
        placeholder="Add a message (optional)"
        value={note}
        disabled={pending}
        onChange={(e) => setNote(e.target.value)}
        multiline
        minRows={3}
        fullWidth
      />

      {/* Selected recipient (uid-safe label) — reassures who receives it. */}
      {recipient ? (
        <Typography>
          Sending to <strong>{recipient.name}</strong>
        </Typography>
      ) : null}

      {recipient?.kind === "conversation" && !recipientWritable ? (
        <Alert severity="info">
          {selectedConversation?.write_access === "friendship_required"
            ? "You must be friends to send messages or files."
            : selectedConversation?.write_access === "read_only"
              ? "This conversation is read-only."
              : "Choose an available conversation to send this post."}
        </Alert>
      ) : null}

      {/* Honest failure — a rejected create/send surfaces here and the dialog STAYS
          open (no fabricated success, no half-open navigation). */}
      {failed ? (
        <GuardianAwareError error={createConversation.error ?? sendMessage.error}>
          <Alert severity="error" role="alert">
            We couldn&apos;t send that. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}

      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button
          type="button"
          variant="contained"
          disabled={!me || !recipientWritable || pending}
          aria-busy={pending || undefined}
          onClick={handleSend}
        >
          {pending ? "Sending…" : "Send"}
        </Button>
      </Stack>
    </Stack>
  );
}

function PlayerRecipientRow({
  player,
  selected,
  disabled,
  onSelect,
}: {
  player: PlayerCardData;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const name = authorLabel(player.display_name, player.user_id);
  const details = [
    typeof player.rating === "number" ? `Rating ${player.rating}` : null,
    player.rating_division ?? null,
    player.relationship ?? null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <ListItem
      divider
      secondaryAction={
        <Button
          type="button"
          variant={selected ? "contained" : "outlined"}
          size="small"
          aria-pressed={selected}
          disabled={disabled}
          onClick={onSelect}
        >
          {selected ? "Selected" : "Select"}
        </Button>
      }
    >
      <ListItemAvatar>
        <Avatar src={player.avatar_url ?? undefined} alt="" aria-hidden />
      </ListItemAvatar>
      <ListItemText primary={name} secondary={details || undefined} />
    </ListItem>
  );
}

/**
 * One recent-conversation row in the picker. The counterpart(s) — everyone whose
 * user_id !== me — are labelled via authorLabel (em-dash when unresolved; NEVER the
 * raw user_id), and a 1:1 shows the counterpart avatar (img, or an initials chip).
 * A selectable <button> with aria-pressed reflects the current choice.
 */
function ConversationRecipientRow({
  conversation,
  me,
  selected,
  disabled,
  onSelect,
}: {
  conversation: ConversationResponse;
  me: string | undefined;
  selected: boolean;
  disabled: boolean;
  onSelect: (name: string) => void;
}) {
  const others: ConversationParticipantSummary[] =
    conversation.participants.filter((p) => p.user_id !== me);
  const name =
    others.length === 0
      ? MISSING
      : others.map((p) => authorLabel(p.display_name, p.user_id)).join(", ");
  const avatarUrl = others.length === 1 ? others[0]?.avatar_url : null;
  const initials = authorInitials(others[0]?.display_name);

  return (
    <ListItem disablePadding>
      <ListItemButton
        aria-pressed={selected}
        selected={selected}
        disabled={disabled}
        onClick={() => onSelect(name)}
      >
        <ListItemAvatar>
          <Avatar src={avatarUrl ?? undefined} alt="" aria-hidden>
            {initials}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={name}
          secondary={
            conversation.write_access === "friendship_required"
              ? "Friendship required"
              : conversation.write_access === "read_only"
                ? "Read-only"
                : undefined
          }
        />
      </ListItemButton>
    </ListItem>
  );
}

// =============================================================================
// Mode B — Share to a community (the repost-into-a-feed)
// =============================================================================

function CommunityShareMode({
  post,
  onClose,
  onBusyChange,
}: {
  post: PostResponse;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const communitiesQ = useCommunities();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [quote, setQuote] = useState("");
  const repost = useCreateRepost();
  // In-flight guard set SYNCHRONOUSLY before the mutate — mirrors Mode-A's
  // `sentRef` (and EditPostDialog / PostComposer). `repost.isPending` only flips
  // true on the next render, so a fast double-click could otherwise fire the repost
  // twice before the button disables; this ref makes the second click a no-op so
  // the repost fires EXACTLY once (not merely deduped by the backend 409).
  const submittingRef = useRef(false);

  const pending = repost.isPending;
  useEffect(() => {
    onBusyChange(pending);
  }, [pending, onBusyChange]);

  // Only the communities the viewer can post to. The backend re-validates
  // membership on the repost (a non-member 400 is the honest fallback).
  const communities = (communitiesQ.data ?? []).filter((c) => c.is_member);

  // A 409 is the "you already reposted this root" conflict — an honest, specific
  // message; any other reject is the generic retry copy.
  const alreadyReposted =
    repost.isError &&
    repost.error instanceof ApiError &&
    repost.error.status === 409;

  function handleShare() {
    // The ref guard fires FIRST (synchronous) so a same-tick double-click can't
    // slip a second repost through before `pending`/disabled re-render.
    if (!selectedId || pending || submittingRef.current) return;
    submittingRef.current = true;
    repost.mutate(
      {
        postId: post.post_id,
        quote: quote.trim() || undefined,
        communityId: selectedId,
      },
      {
        onSuccess: () => onClose(),
        // Honest failure (e.g. a generic reject): release the guard so the viewer
        // can retry. A 409 already-reposted surfaces its own copy above; the button
        // re-enables either way (repost.isPending is false again on error).
        onError: () => {
          submittingRef.current = false;
        },
      },
    );
  }

  const hasCommunities = communities.length > 0;

  return (
    <Stack spacing={2}>
      {communitiesQ.isError && !communitiesQ.data ? (
        <Alert severity="error" role="alert">
          We couldn&apos;t load your communities. Please try again.
        </Alert>
      ) : communitiesQ.isLoading ? (
        <Typography color="text.secondary" role="status" aria-live="polite">
          Loading communities…
        </Typography>
      ) : !hasCommunities ? (
        <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
          <Typography color="text.secondary">
            You&apos;re not in any communities yet.
          </Typography>
          <Button component={RouterLink} to="/community" size="small">
            Explore communities
          </Button>
        </Stack>
      ) : (
        <List disablePadding aria-label="Your communities">
          {communities.map((c) => {
            const selected = selectedId === c.community_id;
            return (
              <ListItem key={c.community_id} disablePadding>
                <ListItemButton
                  aria-pressed={selected}
                  selected={selected}
                  disabled={pending}
                  onClick={() => setSelectedId(c.community_id)}
                >
                  <ListItemAvatar>
                    <Avatar aria-hidden>{communityInitial(c.name)}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={c.name}
                    secondary={
                      c.member_count > 0
                        ? `${c.member_count} ${c.member_count === 1 ? "member" : "members"}`
                        : undefined
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

      {hasCommunities ? (
        <TextField
          label="Add your thoughts (optional)"
          placeholder="Add your thoughts (optional)"
          value={quote}
          disabled={pending}
          onChange={(e) => setQuote(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />
      ) : null}

      {/* Honest failure — a 409 already-reposted has its own specific copy; the
          dialog STAYS open either way (non-optimistic). */}
      {alreadyReposted ? (
        <Alert severity="error" role="alert">
          You&apos;ve already shared this post.
        </Alert>
      ) : repost.isError ? (
        <GuardianAwareError error={repost.error}>
          <Alert severity="error" role="alert">
            We couldn&apos;t share that. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}

      {hasCommunities ? (
        <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
          <Button
            type="button"
            variant="contained"
            disabled={!selectedId || pending}
            aria-busy={pending || undefined}
            onClick={handleShare}
          >
            {pending ? "Sharing…" : "Share"}
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
}

/** The identity-chip glyph for a community — its first alphanumeric character
 *  uppercased, or a neutral dot when the name has none. Purely decorative. */
function communityInitial(name: string | null | undefined): string {
  const ch = (name ?? "")
    .trim()
    .replace(/[^a-z0-9]/gi, "")
    .charAt(0);
  return ch ? ch.toUpperCase() : "•";
}

// =============================================================================
// Shared helpers
// =============================================================================

/**
 * A small local debounce (the app keeps per-surface copies rather than a shared
 * hook): holds `value` stable for `delayMs` after the last change so usePlayerSearch
 * fires once on the settled query, not per keystroke.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
